import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Flex,
  Heading,
  Button,
  Card,
  CardHeader,
  CardBody,
  Grid,
  Text,
  Image,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  VStack,
  HStack,
  useDisclosure,
  useToast,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Spinner,
  Center,
  useColorModeValue,
} from '@chakra-ui/react';
import { useApp } from '../context/AppContext';
import CaseNavigation from './CaseNavigation';

const SampleManagement = () => {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const {
    cases,
    samples,
    addSample,
    updateSample,
    deleteSample,
    canEditSample,
    loadSamplesByCase,
    loading,
    loadingSamples
  } = useApp();
  const toast = useToast();
  const mutedTextColor = useColorModeValue('gray.600', 'gray.300');
  const subtleTextColor = useColorModeValue('gray.500', 'gray.400');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const dashedBorderColor = useColorModeValue('gray.300', 'gray.600');
  const dropActiveBg = useColorModeValue('primary.50', 'primary.900');
  const modalBg = useColorModeValue('white', 'gray.800');
  const labelStrongColor = useColorModeValue('gray.700', 'gray.200');
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onClose: onDeleteClose,
  } = useDisclosure();
  const [editingSample, setEditingSample] = useState(null);
  const [deleteSampleId, setDeleteSampleId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [formData, setFormData] = useState({
    sampleImage: null,
    sampleImagePreview: '',
    description: '',
    surfaceType: 'Porous',
    examinationMaterial: ''
  });

  const revokeObjectUrlIfNeeded = (maybeUrl) => {
    if (typeof maybeUrl === 'string' && maybeUrl.startsWith('blob:')) {
      try {
        URL.revokeObjectURL(maybeUrl);
      } catch {
        // ignore
      }
    }
  };

  // Ensure we compare IDs as strings (API قد يرجعها أرقام)
  const currentCase = cases.find(c => String(c.id) === String(caseId));
  const caseSamples = samples.filter(s => String(s.caseId) === String(caseId));

  // Load samples when component mounts or caseId changes
  useEffect(() => {
    if (caseId) {
      // Check if we already have samples for this case to avoid unnecessary reloads
      const existingSamples = samples.filter(s => String(s.caseId) === String(caseId));
      if (existingSamples.length === 0) {
        loadSamplesByCase(caseId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId]);

  useEffect(() => {
    // لا نعيد التوجيه إلا بعد انتهاء التحميل
    if (!loading && !currentCase) {
      navigate('/cases');
    }
  }, [currentCase, loading, navigate]);

  // أثناء التحميل نعرض لا شيء (أو ممكن نضيف سبينر لاحقاً)
  if (loading) return null;

  if (!currentCase) return null;

  const handleOpenModal = (sample = null) => {
    // Clean up any previous blob preview before opening a new modal session
    revokeObjectUrlIfNeeded(formData.sampleImagePreview);

    if (sample) {
      setEditingSample(sample);
      setFormData({
        sampleImage: null,
        sampleImagePreview: sample.sampleImage || '',
        description: sample.description || '',
        surfaceType: sample.surfaceType || 'Porous',
        examinationMaterial: sample.examinationMaterial || ''
      });
    } else {
      setEditingSample(null);
      setFormData({
        sampleImage: null,
        sampleImagePreview: '',
        description: '',
        surfaceType: 'Porous',
        examinationMaterial: ''
      });
    }
    onOpen();
  };

  const handleCloseModal = () => {
    revokeObjectUrlIfNeeded(formData.sampleImagePreview);
    onClose();
    setEditingSample(null);
    setFormData({
      sampleImage: null,
      sampleImagePreview: '',
      description: '',
      surfaceType: 'Porous',
      examinationMaterial: ''
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      processImageFile(file);
    }
  };

  const processImageFile = (file) => {
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast({
        title: 'خطأ',
        description: 'الملف المحدد ليس صورة',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast({
        title: 'خطأ',
        description: 'حجم الصورة يجب أن يكون أقل من 10MB',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Replace any previous blob preview
    revokeObjectUrlIfNeeded(formData.sampleImagePreview);
    const previewUrl = URL.createObjectURL(file);
    setFormData(prev => ({
      ...prev,
      sampleImage: file,
      sampleImagePreview: previewUrl
    }));
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      processImageFile(files[0]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    try {
      const sampleData = new FormData();
      sampleData.append('caseId', caseId);
      sampleData.append('description', formData.description || '');
      sampleData.append('surfaceType', formData.surfaceType);
      sampleData.append('examinationMaterial', formData.examinationMaterial || '');
      if (formData.sampleImage) {
        sampleData.append('sampleImage', formData.sampleImage);
      }

      if (editingSample) {
        await updateSample(editingSample.id, sampleData);
        toast({
          title: 'تم التحديث',
          description: 'تم تحديث العينة بنجاح',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        await addSample(sampleData);
        toast({
          title: 'تمت الإضافة',
          description: 'تم إضافة العينة بنجاح',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }
      handleCloseModal();
    } catch (error) {
      console.error('Error saving sample:', error);
      // Extract error message from Laravel validation errors
      let errorMessage = 'فشل في حفظ العينة';
      if (error.response?.data) {
        if (error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (error.response.data.errors) {
          // Laravel validation errors format
          const errors = error.response.data.errors;
          const firstError = Object.values(errors)[0];
          errorMessage = Array.isArray(firstError) ? firstError[0] : firstError;
        } else if (error.response.data.error) {
          errorMessage = error.response.data.error;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      toast({
        title: 'خطأ',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteClick = (id) => {
    setDeleteSampleId(id);
    onDeleteOpen();
  };

  const handleDeleteConfirm = async () => {
    if (isDeleting) return;
    
    setIsDeleting(true);
    try {
      await deleteSample(deleteSampleId);
      toast({
        title: 'تم الحذف',
        description: 'تم حذف العينة بنجاح',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      onDeleteClose();
      setDeleteSampleId(null);
    } catch (error) {
      console.error('Error deleting sample:', error);
      toast({
        title: 'خطأ',
        description: error.response?.data?.message || error.message || 'فشل في حذف العينة',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const surfaceTypeLabels = {
    'Porous': 'مسامي',
    'Non-Porous': 'غير مسامي'
  };

  return (
    <Box>
      <CaseNavigation />
      <VStack align="stretch" spacing={4} mb={6}>
        <Button
          onClick={() => {
            // Restore last page from localStorage
            const savedPage = localStorage.getItem('cases_last_page');
            if (savedPage) {
              // Page will be restored when CaseManagement loads
            }
            navigate('/cases');
          }}
          variant="outline"
          size="sm"
          alignSelf="flex-start"
        >
          ← العودة إلى القضايا
        </Button>
        <Flex justify="space-between" align="center">
          <Heading size="lg">إدارة العينات</Heading>
          {canEditSample() && (
            <Button colorScheme="primary" onClick={() => handleOpenModal()}>
              إضافة عينة جديدة
            </Button>
          )}
        </Flex>
      </VStack>

      <Card mb={6}>
        <CardBody>
          <VStack align="stretch" spacing={2}>
            <Heading size="md" mb={2}>معلومات القضية</Heading>
            <Text><strong>رقم القضية:</strong> <span dir="ltr" style={{ display: 'inline-block' }}>{currentCase.caseNumber}</span></Text>
            <Text><strong>موضوع القضية:</strong> {currentCase.caseSubject}</Text>
          </VStack>
        </CardBody>
      </Card>

      {loadingSamples ? (
        <Center py={10}>
          <VStack spacing={4}>
            <Spinner size="xl" color="primary.500" thickness="4px" speed="0.65s" />
            <Text color="gray.600">جاري تحميل العينات...</Text>
          </VStack>
        </Center>
      ) : caseSamples.length === 0 ? (
        <Card>
          <CardBody>
            <Text textAlign="center" color="gray.500">
              لا توجد عينات لهذه القضية
            </Text>
          </CardBody>
        </Card>
      ) : (
        <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }} gap={6}>
          {caseSamples.map((sample) => (
            <Card key={sample.id} boxShadow="md">
              <CardHeader>
                {canEditSample() && (
                  <Flex justify="flex-end">
                    <HStack spacing={2}>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => navigate(`/traces/${caseId}/${sample.id}`)}
                      >
                        الآثار
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleOpenModal(sample)}
                      >
                        تعديل
                      </Button>
                      <Button
                        size="sm"
                        colorScheme="red"
                        onClick={() => handleDeleteClick(sample.id)}
                      >
                        حذف
                      </Button>
                    </HStack>
                  </Flex>
                )}

                <Heading mt={canEditSample() ? 3 : 0} size="xs" color="primary.600" dir="ltr" textAlign="left">
                  {sample.sampleNumber}
                </Heading>
              </CardHeader>
              {sample.sampleImage && (
                <Box p={4}>
                  <Image
                    src={sample.sampleImage}
                    alt="عينة"
                    borderRadius="md"
                    maxH="200px"
                    mx="auto"
                    objectFit="contain"
                  />
                </Box>
              )}
              <CardBody>
                <VStack align="stretch" spacing={2}>
                  <Flex justify="space-between">
                    <Text fontWeight="semibold" fontSize="sm" color="gray.600">
                      الوصف:
                    </Text>
                    <Text fontSize="sm">{sample.description || 'غير محدد'}</Text>
                  </Flex>
                  <Flex justify="space-between">
                    <Text fontWeight="semibold" fontSize="sm" color="gray.600">
                      نوع السطح:
                    </Text>
                    <Text fontSize="sm">
                      {surfaceTypeLabels[sample.surfaceType] || sample.surfaceType}
                    </Text>
                  </Flex>
                  <Flex justify="space-between">
                    <Text fontWeight="semibold" fontSize="sm" color="gray.600">
                      مادة الفحص:
                    </Text>
                    <Text fontSize="sm">{sample.examinationMaterial || 'غير محدد'}</Text>
                  </Flex>
                </VStack>
              </CardBody>
            </Card>
          ))}
        </Grid>
      )}

      <Modal isOpen={isOpen} onClose={handleCloseModal} size="xl">
        <ModalOverlay />
        <ModalContent bg={modalBg}>
          <ModalHeader>
            {editingSample ? 'تعديل عينة' : 'إضافة عينة جديدة'}
          </ModalHeader>
          <ModalCloseButton />
          <form onSubmit={handleSubmit}>
            <ModalBody>
              <VStack spacing={4}>
                <FormControl>
                  <FormLabel>صورة العينة</FormLabel>
                  <Box
                    border="2px dashed"
                    borderColor={isDragging ? 'primary.500' : dashedBorderColor}
                    borderRadius="md"
                    p={4}
                    textAlign="center"
                    cursor="pointer"
                    bg={isDragging ? dropActiveBg : 'transparent'}
                    _hover={{
                      borderColor: 'primary.500',
                      bg: dropActiveBg,
                    }}
                    transition="all 0.2s"
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                  >
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      display="none"
                      id="sample-image-upload"
                    />
                    <Box
                      as="label"
                      htmlFor="sample-image-upload"
                      cursor="pointer"
                      display="block"
                    >
                      <VStack spacing={2}>
                        <Text color={mutedTextColor} fontSize="sm" fontWeight={isDragging ? 'semibold' : 'normal'}>
                          {isDragging 
                            ? 'أفلت الصورة هنا' 
                            : formData.sampleImagePreview 
                            ? 'تغيير الصورة' 
                            : 'انقر لاختيار صورة أو اسحبها هنا'}
                        </Text>
                        {!formData.sampleImagePreview && (
                          <Text fontSize="xs" color={subtleTextColor}>
                            PNG, JPG, GIF حتى 10MB
                          </Text>
                        )}
                      </VStack>
                    </Box>
                  </Box>
                  {formData.sampleImagePreview && (
                    <Box mt={4} borderRadius="md" overflow="hidden" border="1px solid" borderColor={borderColor} position="relative">
                      <Image
                        src={formData.sampleImagePreview}
                        alt="معاينة"
                        maxH="300px"
                        mx="auto"
                        objectFit="contain"
                      />
                      <Button
                        size="xs"
                        colorScheme="red"
                        position="absolute"
                        top={2}
                        right={2}
                        onClick={() => setFormData({ ...formData, sampleImagePreview: '', sampleImage: null })}
                      >
                        حذف
                      </Button>
                    </Box>
                  )}
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>الوصف</FormLabel>
                  <Textarea
                    value={formData.description}
                    onChange={(e) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                    rows={4}
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel fontWeight="semibold" mb={2} color={labelStrongColor}>
                    نوع السطح
                  </FormLabel>
                  <Select
                    value={formData.surfaceType}
                    onChange={(e) =>
                      setFormData({ ...formData, surfaceType: e.target.value })
                    }
                    size="lg"
                    focusBorderColor="primary.500"
                  >
                    <option value="Porous">مسامي</option>
                    <option value="Non-Porous">غير مسامي</option>
                  </Select>
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>مادة الفحص المستخدمة</FormLabel>
                  <Input
                    value={formData.examinationMaterial}
                    onChange={(e) =>
                      setFormData({ ...formData, examinationMaterial: e.target.value })
                    }
                  />
                </FormControl>
              </VStack>
            </ModalBody>
            <Box p={6} borderTop="1px solid" borderColor={borderColor}>
              <HStack justify="flex-end" spacing={3}>
                <Button variant="outline" onClick={handleCloseModal}>
                  إلغاء
                </Button>
                <Button type="submit" colorScheme="primary" isLoading={isSubmitting} loadingText={editingSample ? 'جاري الحفظ...' : 'جاري الإضافة...'}>
                  {editingSample ? 'حفظ التعديلات' : 'إضافة'}
                </Button>
              </HStack>
            </Box>
          </form>
        </ModalContent>
      </Modal>

      <AlertDialog isOpen={isDeleteOpen} onClose={onDeleteClose}>
        <AlertDialogOverlay>
          <AlertDialogContent bg={modalBg}>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              حذف العينة
            </AlertDialogHeader>
            <AlertDialogBody>
              هل أنت متأكد من حذف هذه العينة؟ سيتم حذف جميع الآثار المرتبطة بها.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button onClick={onDeleteClose}>
                إلغاء
              </Button>
              <Button colorScheme="red" onClick={handleDeleteConfirm} mr={3} isLoading={isDeleting} loadingText="جاري الحذف...">
                حذف
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default SampleManagement;
